import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import type {
  AddictionStats,
  MediaItem,
  GoonSessionHistoryRow,
  GoonStats,
  StatHighlightClip,
  StatsActivityPoint,
} from "@gooner/shared";
import { clipKey, tagLabel } from "@gooner/shared";
import { ClipCard } from "../../components/ClipCard";
import { api, userApi } from "../../lib/api";
import { renderSessionCard } from "../../display/sessionCard";
import { fmtCount, fmtDurationSecs } from "../../lib/statFormat";
import { Pager, usePaged } from "../components/Pager";
import { SessionCardDownload } from "../../components/SessionCardDownload";
import { IconPencil, IconTrash } from "../ui/icons";
import { cx } from "../ui/primitives";
import { CrownMark } from "../ui/Crown";
import { GoldName, drawGoldPill } from "../ui/Gold";
import { Avatar } from "../ui/Avatar";
import { Tile } from "../ui/Tile";
import {
  GOLD,
  GOLD_SOFT,
  SITE_HOST,
  drawBrandLockup,
  drawCardGround,
  drawCoverImage,
  drawPill,
  drawQr,
  drawStatTile,
  loadPosters,
  roundRect,
} from "../ui/cardKit";
import { MASK, Masked } from "../../lib/replayMask";

/** Where the share-card QR lands: the owner's public page (/u/:username), which renders
 *  this same body whenever their profile switch is on. The caption beside the QR must
 *  keep describing that destination — and it can now promise the scanner the real page,
 *  because a public profile shows the same numbers the card does. */
const PROFILE_URL = (username: string) =>
  `https://${SITE_HOST}/u/${encodeURIComponent(username)}`;

const CARD_W = 1080;
const CARD_H = 1350; // 4:5 — the social-feed sweet spot

/** Bucket daily activity into Monday-based week columns (like the on-page heatmap). */
function activityWeeks(
  points: StatsActivityPoint[],
): (StatsActivityPoint | null)[][] {
  const dow = (date: string) => {
    const [y, m, d] = date.split("-").map(Number);
    return (new Date(y, m - 1, d).getDay() + 6) % 7;
  };
  const weeks: (StatsActivityPoint | null)[][] = [];
  let col: (StatsActivityPoint | null)[] = points.length
    ? new Array<StatsActivityPoint | null>(dow(points[0].date)).fill(null)
    : [];
  for (const p of points) {
    col.push(p);
    if (col.length === 7) {
      weeks.push(col);
      col = [];
    }
  }
  if (col.length) {
    while (col.length < 7) col.push(null);
    weeks.push(col);
  }
  return weeks;
}

/**
 * Render a rich 1080×1350 "Gooner Wrapped" share card: identity + tier, the addiction
 * gauge, the out-goon percentile hook, headline stats, a daily-activity heatmap, top
 * categories, and a QR + URL. Returns the canvas (caller downloads / previews it).
 *
 * The primitives it draws with — ground, lockup, tiles, QR, poster decoding and their
 * fail-closed rules — live in ui/cardKit.ts, shared with the session card.
 */
async function renderStatsCard(
  username: string,
  stats: GoonStats,
  gold: boolean,
  /** Whether the QR's destination will actually show these numbers — the caption has to
   *  promise what the scanner will really find, and that depends on the owner's switch. */
  profilePublic: boolean,
): Promise<HTMLCanvasElement> {
  const images = await loadPosters(
    [stats.mostWatched, stats.mostFapped, stats.mostLooped]
      .filter((h): h is StatHighlightClip => !!h)
      .map((h) => ({ id: h.clip.id, posterUrl: h.clip.posterUrl })),
  );
  const canvas = document.createElement("canvas");
  canvas.width = CARD_W;
  canvas.height = CARD_H;
  const ctx = canvas.getContext("2d");
  if (!ctx) return canvas;
  const M = 72;
  const a = stats.addiction;
  ctx.textBaseline = "alphabetic";

  // ---- background: ink + gold glow + framed border ----
  drawCardGround(ctx, CARD_W, CARD_H);

  // ---- header: brand + tier pill ----
  // Same lockup as the session card: mark, gap, words — left-aligned from the margin.
  drawBrandLockup(ctx, M, 116, 40);
  const tier = (a.level || "").toUpperCase();
  if (tier) {
    ctx.font = "bold 27px system-ui, sans-serif";
    const pillW = ctx.measureText(tier).width + 54;
    drawPill(ctx, tier, CARD_W - M - pillW, 80, pillW, 48, 27);
  }

  // ---- username ----
  ctx.textAlign = "left";
  ctx.fillStyle = "#fafafa";
  ctx.font = "900 78px system-ui, sans-serif";
  ctx.fillText(`@${username}`, M, 226);
  if (gold) {
    // The PAID tier's chip, beside the handle. Distinct from the EARNED tier pill in
    // the top-right corner (ADDICTION_TIERS — "Goon Lord" is a rank you goon your way
    // into); a maxed-out Gold member legitimately wears both.
    const nameW = ctx.measureText(`@${username}`).width;
    drawGoldPill(ctx, M + nameW + 26, 226 - 52, 52);
  }

  // ---- hero: addiction gauge + out-goon percentile ----
  const gcx = M + 104;
  const gcy = 378;
  const gr = 94;
  ctx.lineWidth = 18;
  ctx.lineCap = "round";
  ctx.strokeStyle = "#26262b";
  ctx.beginPath();
  ctx.arc(gcx, gcy, gr, 0, Math.PI * 2);
  ctx.stroke();
  const score = Math.max(0, Math.min(100, a.score));
  ctx.strokeStyle = GOLD;
  ctx.beginPath();
  ctx.arc(
    gcx,
    gcy,
    gr,
    -Math.PI / 2,
    -Math.PI / 2 + (Math.PI * 2 * score) / 100,
  );
  ctx.stroke();
  ctx.textAlign = "center";
  ctx.fillStyle = "#fafafa";
  ctx.font = "bold 62px system-ui, sans-serif";
  ctx.fillText(String(a.score), gcx, gcy + 6);
  ctx.fillStyle = GOLD_SOFT;
  ctx.font = "700 20px system-ui, sans-serif";
  ctx.fillText("ADDICTION", gcx, gcy + 40);
  const px = gcx + gr + 54;
  ctx.textAlign = "left";
  ctx.fillStyle = "#a1a1aa";
  ctx.font = "600 28px system-ui, sans-serif";
  ctx.fillText("OUT-GOONS", px, 322);
  ctx.fillStyle = GOLD;
  ctx.font = "900 106px system-ui, sans-serif";
  ctx.fillText(`${a.time.pct}%`, px, 418);
  ctx.fillStyle = "#a1a1aa";
  ctx.font = "500 25px system-ui, sans-serif";
  ctx.fillText(
    a.totalUsers
      ? `of all gooners · #${a.rank} of ${fmtCount(a.totalUsers)}`
      : "of all gooners",
    px,
    456,
  );

  // ---- headline stat tiles ----
  const tiles: Array<[string, string, boolean]> = [
    ["Time gooned", fmtDurationSecs(stats.totalSeconds), false],
    ["Clips", fmtCount(stats.clipsWatched), false],
    ["Edges", fmtCount(stats.edges), true],
    ["Faps", fmtCount(stats.faps), false],
    ["Loops", fmtCount(stats.loops), false],
  ];
  const tY = 512;
  const tH = 122;
  const gap = 16;
  // Derived from tiles.length rather than the literal 4 it was: a fifth tile used to mean
  // editing two numbers that did not say what they were, and the heatmap below keys off
  // tY + tH, not off the count, so widening the row is the only thing this touches.
  const tW = (CARD_W - 2 * M - (tiles.length - 1) * gap) / tiles.length;
  tiles.forEach(([label, value, hot], i) => {
    drawStatTile(ctx, M + i * (tW + gap), tY, tW, tH, value, label, hot);
  });

  // ---- one-line insights ----
  const bits: string[] = [];
  if (stats.longestStreak) bits.push(`${stats.longestStreak}-day streak`);
  if (stats.sessions?.longestSec)
    bits.push(`${fmtDurationSecs(stats.sessions.longestSec)} longest session`);
  if (stats.favoriteCategory) bits.push(`loves ${stats.favoriteCategory}`);
  ctx.textAlign = "left";
  ctx.fillStyle = "#a1a1aa";
  ctx.font = "500 25px system-ui, sans-serif";
  ctx.fillText(bits.slice(0, 3).join("   ·   "), M, tY + tH + 54);

  // ---- daily-activity heatmap ----
  let y = tY + tH + 116; // ~750
  ctx.fillStyle = GOLD_SOFT;
  ctx.font = "700 23px system-ui, sans-serif";
  ctx.fillText("DAILY ACTIVITY · LAST 12 MONTHS", M, y - 14);
  const weeks = activityWeeks(stats.activity ?? []);
  const maxSec = Math.max(1, ...(stats.activity ?? []).map((p) => p.seconds));
  const cols = Math.max(weeks.length, 1);
  const cg = 3;
  const cell = Math.max(
    6,
    Math.floor((CARD_W - 2 * M - (cols - 1) * cg) / cols),
  );
  weeks.forEach((w, ci) => {
    w.forEach((c, ri) => {
      if (!c) return;
      const r = c.seconds / maxSec;
      ctx.fillStyle =
        c.seconds <= 0
          ? "#1a1a1e"
          : `rgba(245,197,24,${r < 0.25 ? 0.3 : r < 0.5 ? 0.55 : r < 0.75 ? 0.78 : 1})`;
      roundRect(ctx, M + ci * (cell + cg), y + ri * (cell + cg), cell, cell, 3);
      ctx.fill();
    });
  });
  y += 7 * (cell + cg) + 46;

  // ---- highlight thumbnails ----
  //
  // This replaced the TOP CATEGORIES bar chart, which is where the room came from: with
  // three categories that block ran to y≈1083 and left ~55px before the QR, nowhere near
  // enough for images. The favourite category still rides the insights line above, so
  // nothing is lost that the card did not say twice.
  const shots: Array<[string, StatHighlightClip | null, string]> = [
    ["Most watched", stats.mostWatched, `${fmtDurationSecs(stats.mostWatched?.seconds ?? 0)}`],
    [
      "Most fapped",
      stats.mostFapped,
      `${fmtCount(stats.mostFapped?.clicks ?? 0)} tap${stats.mostFapped?.clicks === 1 ? "" : "s"}`,
    ],
    [
      "Most looped",
      stats.mostLooped,
      `${fmtCount(stats.mostLooped?.loops ?? 0)} loop${stats.mostLooped?.loops === 1 ? "" : "s"}`,
    ],
  ];
  if (shots.some(([, h]) => h)) {
    ctx.textAlign = "left";
    ctx.fillStyle = GOLD_SOFT;
    ctx.font = "700 23px system-ui, sans-serif";
    ctx.fillText("ON REPEAT", M, y);
    y += 26;
    const sGap = 16;
    const sW = (CARD_W - 2 * M - 2 * sGap) / 3;
    const sH = 150;
    shots.forEach(([label, hl, caption], i) => {
      const x = M + i * (sW + sGap);
      const img = hl ? images.get(hl.clip.id) : undefined;
      if (img) drawCoverImage(ctx, img, x, y, sW, sH, 18);
      else {
        ctx.save();
        roundRect(ctx, x, y, sW, sH, 18);
        ctx.clip();
        ctx.fillStyle = "#141417";
        ctx.fillRect(x, y, sW, sH);
        ctx.restore();
      }
      roundRect(ctx, x, y, sW, sH, 18);
      ctx.strokeStyle = "#232327";
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.textAlign = "left";
      ctx.fillStyle = img ? "#fafafa" : "#71717a";
      ctx.font = "600 20px system-ui, sans-serif";
      ctx.fillText(label.toUpperCase(), x + 16, y + 30);
      if (hl) {
        ctx.fillStyle = img ? "#fafafa" : "#d4d4d8";
        ctx.font = "bold 26px system-ui, sans-serif";
        ctx.fillText(caption, x + 16, y + sH - 18);
      } else {
        ctx.fillStyle = "#3f3f46";
        ctx.font = "500 22px system-ui, sans-serif";
        ctx.fillText("—", x + 16, y + sH - 18);
      }
    });
    y += sH + 20;
  }

  // ---- footer: QR + URL hook (pinned to the bottom) ----
  const qs = 152;
  const qy = CARD_H - 60 - qs;
  drawQr(ctx, PROFILE_URL(username), M, qy, qs);
  const fx = M + qs + 40;
  ctx.textAlign = "left";
  ctx.fillStyle = "#a1a1aa";
  ctx.font = "600 26px system-ui, sans-serif";
  // Both halves have to be true. A public profile really does show these numbers to the
  // scanner; a private one shows a name and some collections, so it may only promise the
  // page — the "your own stats" half is the CTA there, and that is always on offer.
  ctx.fillText(
    profilePublic
      ? "SCAN TO SEE MY STATS — AND YOUR OWN"
      : "SCAN TO SEE MY PAGE — AND YOUR OWN STATS",
    fx,
    qy + 52,
  );
  ctx.fillStyle = GOLD;
  ctx.font = "900 54px system-ui, sans-serif";
  ctx.fillText(SITE_HOST, fx, qy + 110);
  ctx.fillStyle = "#71717a";
  ctx.font = "500 24px system-ui, sans-serif";
  ctx.fillText("Free to browse · no signup to look", fx, qy + 146);

  return canvas;
}

/** Preview + download the share card. Renders once on open (canvas → PNG data URL).
 *
 *  Reached only through ShareCardButton, which only the owner's page mounts — so this
 *  always has its Download and never needs to ask who is looking. */
function ShareCardModal({
  username,
  stats,
  gold,
  profilePublic,
  onClose,
}: {
  username: string;
  stats: GoonStats;
  gold: boolean;
  profilePublic: boolean;
  onClose: () => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    // The render waits on poster decodes now, so this is a cancellable async job rather
    // than a deferred tick: without the flag a slow decode resolves into an unmounted
    // component. toDataURL is wrapped because a tainted canvas throws SecurityError, and
    // an unhandled one here is a spinner that never resolves and never says why.
    let cancelled = false;
    void (async () => {
      try {
        const canvas = await renderStatsCard(username, stats, gold, profilePublic);
        if (cancelled) return;
        setUrl(canvas.toDataURL("image/png"));
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [username, stats, gold, profilePublic]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm space-y-4 rounded-2xl border border-[var(--color-edge)] bg-[var(--color-panel)] p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-sm font-semibold text-zinc-200">Your share card</h2>
        {failed ? (
          <div className="flex aspect-[4/5] items-center justify-center rounded-xl bg-[var(--color-ink)] px-6 text-center text-sm text-zinc-400">
            The card couldn&rsquo;t be drawn. Reload and try again.
          </div>
        ) : url ? (
          <img
            src={url}
            alt="Your goonlords stats card"
            className="w-full rounded-xl border border-[var(--color-edge)]"
          />
        ) : (
          <div className="flex aspect-[4/5] items-center justify-center rounded-xl bg-[var(--color-ink)]">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-[var(--color-edge)] border-t-[var(--color-accent)]" />
          </div>
        )}
        <div className="flex gap-2">
          <a
            href={url ?? "#"}
            download={`goonlords-${username}.png`}
            className={cx(
              "flex-1 rounded-lg bg-[var(--color-accent)] px-4 py-2 text-center text-sm font-semibold text-white hover:opacity-90",
              !url && "pointer-events-none opacity-50",
            )}
          >
            Download
          </a>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-[var(--color-edge)] px-4 py-2 text-sm text-zinc-300 hover:bg-[var(--color-panel-2)]"
          >
            Close
          </button>
        </div>
        <p className="text-center text-[11px] text-zinc-500">
          Post it and tag us — friends scan the QR to see how they stack up.
        </p>
      </div>
    </div>
  );
}

/**
 * "Share card ↗" and the modal it opens, as ONE exported unit.
 *
 * One export rather than a button plus a `downloadable` flag, because the download IS
 * the share card: the public page removes it by never mounting this, not by passing
 * false. A boolean would leave the owner's PNG one wrong default away from appearing on
 * a page its owner never opted into — and the visitor already has the "See your own
 * stats" CTA, which is the door the card's own caption promises them.
 */
export function ShareCardButton({
  username,
  stats,
  gold,
  profilePublic,
}: {
  username: string;
  stats: GoonStats;
  gold: boolean;
  /** Whether /u/<username> will really show these numbers — the QR caption says so. */
  profilePublic: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="rounded-lg bg-[var(--color-accent)] px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
      >
        Share card ↗
      </button>
      {open ? (
        <ShareCardModal
          username={username}
          stats={stats}
          gold={gold}
          profilePublic={profilePublic}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}

/**
 * The profile itself — every tile, gauge, highlight and graph — rendered from props.
 *
 * Two routes mount this: `/profile`, where it is the signed-in owner looking at their
 * own numbers, and `/u/:username`, where it is whoever opened a lord's public page.
 * Nothing in here reads the session, and that is the point: on the public page the
 * signed-in user is the WRONG person, so identity arrives as `username`/`gold` beside
 * the stats they belong to.
 *
 * Owner affordances are opt-in props, the idiom CollectionView established — absent
 * means the public view. `isOwner` is what changes the copy from "Your" to a name;
 * `ownerActions` is the slot the profile page hangs its public/private switch in.
 */
export function ProfileBody({
  username,
  gold,
  avatarUrl,
  onEditAvatar,
  stats,
  isOwner = false,
  ownerActions,
  onStatsChanged,
}: {
  username: string;
  gold: boolean;
  /** Their picture, when set; absent = the initials disc. */
  avatarUrl?: string;
  /** Owner-only: the disc becomes a button that opens the picture editor. */
  onEditAvatar?: () => void;
  stats: GoonStats;
  /** The viewer is the person these stats belong to. COPY ONLY — it never gates data
   *  (the server already decided what is in `stats`) and it never grants an action
   *  (those arrive as `ownerActions`). Get it wrong and you leak a pronoun. */
  isOwner?: boolean;
  /** Owner-only controls for the header row: the visibility switch and the share card.
   *  Absent is the public view — which is how the card's download stays off it. */
  ownerActions?: ReactNode;
  /** The owner pruned a session: refetch the numbers, which read the table live. */
  onStatsChanged?: () => void;
}) {
  // Second person on your own page, the lord's name on anyone else's. The headings are
  // the only place the page speaks TO someone, so they are the only place this matters.
  // On a visitor's page the lord's name in it is masked in session replay; "Your" is not.
  const poss = isOwner ? "Your" : <><Masked>{username}</Masked>{"'s"}</>;
  // What a highlight's popup pages through: the highlights, each clip once — the most
  // watched clip is often the most fapped one too.
  const highlightClips = uniqueClips(
    [stats.mostWatched, stats.mostEdged, stats.mostFapped, stats.mostLooped].map((h) => h?.clip),
  );

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {onEditAvatar ? (
            <button
              type="button"
              onClick={onEditAvatar}
              title="Change your picture"
              aria-label="Change your picture"
              className="group relative rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-soft)]/60"
            >
              <Avatar name={username} src={avatarUrl} size="lg" />
              <span className="absolute -bottom-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full border border-[var(--color-edge)] bg-[var(--color-panel)] text-zinc-300 group-hover:text-white">
                <IconPencil width={10} height={10} />
              </span>
            </button>
          ) : (
            <Avatar name={username} src={avatarUrl} size="lg" />
          )}
          <div>
            <h1 className="flex items-center text-lg font-semibold text-zinc-100">
              <GoldName name={username} gold={gold} />
            </h1>
            <p className="text-xs text-zinc-500">
              {isOwner ? "Your gooning profile" : "Gooning profile"}
            </p>
          </div>
        </div>
        {ownerActions ? (
          <div className="flex items-center gap-2">{ownerActions}</div>
        ) : null}
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Tile label="Addiction level" value={stats.addiction.level} accent />
        <Tile
          label="Global rank"
          value={
            stats.addiction.totalUsers
              ? `#${stats.addiction.rank} of ${fmtCount(stats.addiction.totalUsers)}`
              : "—"
          }
        />
        <Tile
          label="Out-goon"
          value={`${stats.addiction.time.pct}% of gooners`}
          accent
        />
        <Tile label="Time gooned" value={fmtDurationSecs(stats.totalSeconds)} />
        <Tile label="Clips watched" value={fmtCount(stats.clipsWatched)} />
        <Tile label="Clips fapped" value={fmtCount(stats.faps)} />
        <Tile label="Edges" value={fmtCount(stats.edges)} accent />
        <Tile label="Favorite category" value={stats.favoriteCategory ?? "—"} />
      </div>

      <section>
        <h2 className="mb-3 text-sm font-semibold tracking-wide text-zinc-300">
          {poss} addiction level
        </h2>
        <AddictionSection a={stats.addiction} subject={isOwner ? "You out-goon" : <><Masked>{username}</Masked> out-goons</>} />
      </section>

      <section>
        <h2 className="mb-3 text-sm font-semibold tracking-wide text-zinc-300">
          {poss} highlights
        </h2>
        {stats.mostWatched || stats.mostFapped || stats.mostEdged || stats.mostLooped ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {stats.mostWatched ? (
              <HighlightCard
                label="Most watched"
                highlight={stats.mostWatched}
                list={highlightClips}
                caption={`${fmtDurationSecs(stats.mostWatched.seconds ?? 0)} · ${stats.mostWatched.plays ?? 0} play${stats.mostWatched.plays === 1 ? "" : "s"}`}
              />
            ) : null}
            {stats.mostEdged ? (
              <HighlightCard
                label="Most edged"
                accent
                highlight={stats.mostEdged}
                list={highlightClips}
                caption={`${fmtCount(stats.mostEdged.edges ?? 0)} edge${stats.mostEdged.edges === 1 ? "" : "s"}`}
              />
            ) : null}
            {stats.mostFapped ? (
              <HighlightCard
                label="Most fapped"
                highlight={stats.mostFapped}
                list={highlightClips}
                caption={
                  <span className="inline-flex items-center gap-1">
                    <CrownMark size={12} />
                    {fmtCount(stats.mostFapped.clicks ?? 0)}
                  </span>
                }
              />
            ) : null}
            {stats.mostLooped ? (
              <HighlightCard
                label="Most looped"
                highlight={stats.mostLooped}
                list={highlightClips}
                caption={`${fmtCount(stats.mostLooped.loops ?? 0)} loop${stats.mostLooped.loops === 1 ? "" : "s"}`}
              />
            ) : null}
          </div>
        ) : (
          <EmptyHint>
            {isOwner
              ? "Start a session, fap a few clips, loop the ones worth repeating — your highlights show up here."
              : "No public highlights to show."}
          </EmptyHint>
        )}
      </section>

      <div className="grid gap-6 md:grid-cols-2">
        <section>
          <h2 className="mb-3 text-sm font-semibold tracking-wide text-zinc-300">
            Favorite collection
          </h2>
          {stats.favoriteCollection ? (
            <Link
              to={`/browse/${stats.favoriteCollection.id}`}
              className="flex items-center justify-between rounded-xl border border-[var(--color-edge)] bg-[var(--color-panel)] p-4 transition-colors hover:border-[var(--color-accent)]/50"
            >
              <div className="min-w-0">
                <div className="truncate text-base font-semibold text-zinc-100" {...MASK}>
                  {stats.favoriteCollection.name}
                </div>
                <div className="text-xs text-zinc-500">
                  {stats.favoriteCollection.clipCount} clip
                  {stats.favoriteCollection.clipCount === 1 ? "" : "s"}
                </div>
              </div>
              <CrownMark size={18} className="ml-3 shrink-0" />
            </Link>
          ) : (
            <EmptyHint>
              {isOwner ? (
                <>
                  You haven&rsquo;t built a collection yet.{" "}
                  <Link
                    to="/collections"
                    className="text-[var(--color-accent-soft)] underline"
                  >
                    Start one
                  </Link>
                  .
                </>
              ) : (
                "No public collection to show."
              )}
            </EmptyHint>
          )}
        </section>

        <section>
          <h2 className="mb-3 text-sm font-semibold tracking-wide text-zinc-300">
            Top keywords
          </h2>
          {stats.topKeywords.length > 0 ? (
            <div className="flex flex-wrap gap-2 rounded-xl border border-[var(--color-edge)] bg-[var(--color-panel)] p-4">
              {stats.topKeywords.map((k, i) => (
                <span
                  key={k.tag}
                  className={cx(
                    "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs",
                    i === 0
                      ? "border-[var(--color-accent)]/50 bg-[var(--color-accent)]/10 text-[var(--color-accent-soft)]"
                      : "border-[var(--color-edge)] bg-[var(--color-panel-2)] text-zinc-300",
                  )}
                >
                  {/* The display name, not the slug: `big-tits` is storage, "Big Tits"
                      is what every other keyword chip on the site says. tagLabel is
                      facet-aware too, so `tits:medium` reads "Medium Tits" and
                      `actor_count:2` reads "2 People" rather than the raw slug. */}
                  {tagLabel(k.tag)}
                  <span className="text-zinc-500">{k.count}</span>
                </span>
              ))}
            </div>
          ) : (
            <EmptyHint>
              {isOwner
                ? "Watch more clips to surface the keywords you love most."
                : <><Masked>{username}</Masked>{" hasn't watched enough to have keywords yet."}</>}
            </EmptyHint>
          )}
        </section>
      </div>

      <section>
        <h2 className="mb-3 text-sm font-semibold tracking-wide text-zinc-300">
          Sessions &amp; streaks
        </h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Tile label="Goon sessions" value={fmtCount(stats.sessions.count)} />
          <Tile
            label="Longest session"
            value={fmtDurationSecs(stats.sessions.longestSec)}
            accent
          />
          <Tile
            label="Avg session"
            value={fmtDurationSecs(stats.sessions.avgSec)}
          />
          <Tile
            label="Shortest session"
            value={fmtDurationSecs(stats.sessions.shortestSec)}
          />
          <Tile
            label="Current streak"
            value={fmtDays(stats.currentStreak)}
            accent
          />
          <Tile label="Longest streak" value={fmtDays(stats.longestStreak)} />
          <Tile label="Best day" value={fmtDurationSecs(stats.bestDaySeconds)} />
        </div>
      </section>

      {/* OWNER-ONLY, and only where the page can refetch its tiles: /profile hands
          `onStatsChanged` in; /u/:name never does — even for the owner looking at their
          own public page, which is the visitor's view of them, not the place to prune.
          A visitor's stats carry no session ids anyway, and the route is bearer-only.
          This is where the 12h24m session nobody had gets pruned. */}
      {isOwner && onStatsChanged ? (
        <SessionHistory username={username} onChanged={onStatsChanged} />
      ) : null}

      <section>
        <h2 className="mb-3 text-sm font-semibold tracking-wide text-zinc-300">
          Activity — last 12 months
        </h2>
        <ContributionGraph points={stats.activity} />
      </section>

      {stats.categoryBreakdown.length > 0 ? (
        <section>
          <h2 className="mb-3 text-sm font-semibold tracking-wide text-zinc-300">
            Watch time by category
          </h2>
          <CategoryBreakdown data={stats.categoryBreakdown} />
        </section>
      ) : null}
    </div>
  );
}

/** Day-count formatter for streaks. */
function fmtDays(n: number): string {
  return `${n} day${n === 1 ? "" : "s"}`;
}

/** The gamified "addiction level": a score gauge, a tier track, and percentile bars. */
function AddictionSection({
  a,
  subject,
}: {
  a: AddictionStats;
  /** Who the sentence is about: "You" on your own page, "@name" on a visitor's.
   *  Carries the verb with it — "You out-goon" but "@name out-goons". */
  subject: ReactNode;
}) {
  const bars: Array<{ label: string; pct: number; detail: string }> = [
    {
      label: "Time on platform",
      pct: a.time.pct,
      detail: `ranked #${a.time.rank}`,
    },
    {
      label: "Videos consumed",
      pct: a.clips.pct,
      detail: `ranked #${a.clips.rank}`,
    },
    { label: "Edges", pct: a.edges.pct, detail: `ranked #${a.edges.rank}` },
    { label: "Clips fapped", pct: a.faps.pct, detail: `ranked #${a.faps.rank}` },
    { label: "Crown taps", pct: a.taps.pct, detail: `ranked #${a.taps.rank}` },
    { label: "Faps balance", pct: a.wealth.pct, detail: `ranked #${a.wealth.rank}` },
  ];
  return (
    <div className="rounded-xl border border-[var(--color-edge)] bg-[var(--color-panel)] p-5">
      <div className="flex flex-col gap-6 sm:flex-row sm:items-center">
        <Gauge score={a.score} level={a.level} />
        <div className="min-w-0 flex-1">
          <p className="text-sm text-zinc-300">
            {subject}{" "}
            <span className="font-semibold text-[var(--color-accent-soft)]">
              {a.time.pct}% of gooners
            </span>{" "}
            by time on the platform
            {a.totalUsers ? (
              <>
                {" "}
                — ranked{" "}
                <span className="font-semibold text-zinc-100">
                  #{a.rank} of {fmtCount(a.totalUsers)}
                </span>
              </>
            ) : null}
            .
          </p>
          <TierTrack
            index={a.levelIndex}
            count={a.levelCount}
            level={a.level}
          />
        </div>
      </div>
      <div className="mt-5 grid gap-x-8 gap-y-4 sm:grid-cols-2">
        {bars.map((b) => (
          <PercentileBar
            key={b.label}
            label={b.label}
            pct={b.pct}
            detail={b.detail}
          />
        ))}
      </div>
    </div>
  );
}

/** Circular score gauge with the tier name in the center. */
function Gauge({ score, level }: { score: number; level: string }) {
  const R = 52;
  const C = 2 * Math.PI * R;
  const dash = (C * Math.max(0, Math.min(100, score))) / 100;
  return (
    <div className="relative mx-auto h-32 w-32 shrink-0">
      <svg viewBox="0 0 120 120" className="h-32 w-32 -rotate-90">
        <circle
          cx="60"
          cy="60"
          r={R}
          fill="none"
          stroke="var(--color-panel-2)"
          strokeWidth="12"
        />
        <circle
          cx="60"
          cy="60"
          r={R}
          fill="none"
          stroke="var(--color-accent)"
          strokeWidth="12"
          strokeLinecap="round"
          strokeDasharray={`${dash} ${C}`}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        <span className="text-3xl font-bold text-zinc-100">{score}</span>
        <span className="px-2 text-[10px] font-semibold uppercase leading-tight tracking-wide text-[var(--color-accent-soft)]">
          {level}
        </span>
      </div>
    </div>
  );
}

/** A pip track showing progress through the addiction tiers. */
function TierTrack({
  index,
  count,
  level,
}: {
  index: number;
  count: number;
  level: string;
}) {
  return (
    <div className="mt-3">
      <div className="flex gap-1">
        {Array.from({ length: count }, (_, i) => (
          <div
            key={i}
            className={cx(
              "h-1.5 flex-1 rounded-full",
              i <= index
                ? "bg-[var(--color-accent)]"
                : "bg-[var(--color-panel-2)]",
            )}
          />
        ))}
      </div>
      <div className="mt-1.5 flex justify-between text-[10px] text-zinc-500">
        <span>Fresh Meat</span>
        <span className="font-semibold text-[var(--color-accent-soft)]">
          {level} · tier {index + 1}/{count}
        </span>
        <span>Goon Lord</span>
      </div>
    </div>
  );
}

/** A horizontal "you beat X% of gooners" bar. */
function PercentileBar({
  label,
  pct,
  detail,
}: {
  label: string;
  pct: number;
  detail: string;
}) {
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between">
        <span className="text-xs font-medium text-zinc-300">{label}</span>
        <span className="text-xs font-semibold text-[var(--color-accent-soft)]">
          top {Math.max(1, 100 - pct)}%
        </span>
      </div>
      <div className="h-2.5 overflow-hidden rounded-full bg-[var(--color-panel-2)]">
        <div
          className="h-full rounded-full bg-[var(--color-accent)]"
          style={{ width: `${Math.max(2, pct)}%` }}
        />
      </div>
      <div className="mt-1 text-[11px] text-zinc-500">
        more than {pct}% of gooners · {detail}
      </div>
    </div>
  );
}

/** Compact number formatter: 1234 → "1.2k". */
/** Distinct clips in order, gaps dropped. */
function uniqueClips(clips: Array<MediaItem | undefined>): MediaItem[] {
  const seen = new Set<string>();
  return clips.filter((c): c is MediaItem => {
    if (!c || seen.has(clipKey(c))) return false;
    seen.add(clipKey(c));
    return true;
  });
}

/** A clip card with a metric caption — the profile's "highlight" cards. The same card as
 *  Browse, so a visitor can open, fap, or collect someone else's highlight. */
function HighlightCard({
  label,
  highlight,
  list,
  caption,
  accent,
}: {
  label: string;
  highlight: StatHighlightClip;
  /** Every highlight on the page, for the popup's ‹ ›. */
  list: MediaItem[];
  /** A node, not only a string: the most-fapped card's caption is a number and the
   *  crown — the way a fap count reads everywhere now, never "16 taps". */
  caption: ReactNode;
  accent?: boolean;
}) {
  return (
    <div>
      <ClipCard
        clip={highlight.clip}
        list={list}
        surface="profile"
        badge={
          <span
            className={cx(
              "rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white",
              accent ? "bg-[var(--color-accent)]" : "bg-black/70",
            )}
          >
            {label}
          </span>
        }
      />
      <div
        className={cx(
          "mt-1.5 text-center text-xs font-medium",
          accent ? "text-[var(--color-accent-soft)]" : "text-zinc-400",
        )}
      >
        {caption}
      </div>
    </div>
  );
}

/** A calendar date the way the profile speaks — short, local, no time. */
function fmtDate(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

/**
 * The owner's finished sessions, newest first, each with the card and a delete.
 *
 * The card is the SAME card the screen pops when a session stops (renderSessionCard,
 * through the same summary read) — a person who closed that recap too fast gets it back
 * here. Delete is a two-step in the row rather than a confirm dialog: the codebase has
 * no modal for that, and a row asking "Delete this session?" beside the thing it means
 * is clearer than a box floating over the page. Both actions refetch: the list, and the
 * stats tiles above through `onChanged`, which read the table live.
 */
/** Three rows a page, every width. A history is an archive, not a menu: the point of
 *  the list is the latest few and the ability to prune, and a wall of every session
 *  since August pushes the heatmap below it out of reach. The same usePaged/Pager the
 *  collection shelves use, so deleting the last row on the last page steps back a page
 *  instead of stranding an empty one. */
const HISTORY_PAGE = 3;

function SessionHistory({ username, onChanged }: { username: string; onChanged?: () => void }) {
  const [rows, setRows] = useState<GoonSessionHistoryRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [card, setCard] = useState<{ id: string; url: string | null; failed: boolean } | null>(null);
  const paged = usePaged(rows ?? [], HISTORY_PAGE);

  const load = () => {
    userApi.session
      .history()
      .then((r) => setRows(r.sessions))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  };
  useEffect(load, []);

  async function remove(id: string) {
    setBusyId(id);
    try {
      await userApi.session.remove(id);
      setConfirmId(null);
      load();
      onChanged?.();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message.replace(/^\d+:\s*/, "") : String(e));
    } finally {
      setBusyId(null);
    }
  }

  // Fetch, then draw — the recap's own two halves. toDataURL is wrapped because a tainted
  // canvas throws, and an unhandled one here is a spinner that never resolves.
  async function openCard(id: string) {
    setCard({ id, url: null, failed: false });
    try {
      const r = await api.session.summary(id);
      if (!r.summary) throw new Error("no summary");
      const canvas = await renderSessionCard(r.summary, username);
      setCard((c) => (c?.id === id ? { id, url: canvas.toDataURL("image/png"), failed: false } : c));
    } catch {
      setCard((c) => (c?.id === id ? { id, url: null, failed: true } : c));
    }
  }

  return (
    <section>
      <h2 className="mb-3 text-sm font-semibold tracking-wide text-zinc-300">Session history</h2>
      {error ? <p className="mb-2 text-sm text-red-400">{error}</p> : null}
      {rows === null ? (
        <div className="h-10 animate-pulse rounded-xl bg-[var(--color-panel)]" />
      ) : rows.length === 0 ? (
        <EmptyHint>No finished sessions yet — start one from a screen&rsquo;s Goon button.</EmptyHint>
      ) : (
        <>
        <ul className="divide-y divide-[var(--color-edge)] rounded-xl border border-[var(--color-edge)] bg-[var(--color-panel)]">
          {paged.shown.map((s) => (
            <li key={s.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 text-sm">
              <span className="w-32 shrink-0 text-zinc-300">{fmtDate(s.endedAt)}</span>
              <span className="w-20 shrink-0 font-semibold tabular-nums text-zinc-100">{fmtDurationSecs(s.durationSec)}</span>
              <span className="text-zinc-500">
                {s.edges} {s.edges === 1 ? "edge" : "edges"} · {s.views} {s.views === 1 ? "clip" : "clips"}
              </span>
              <span className="ml-auto flex items-center gap-2">
                {confirmId === s.id ? (
                  <>
                    <span className="text-zinc-300">Delete this session?</span>
                    <button
                      type="button"
                      disabled={busyId === s.id}
                      onClick={() => void remove(s.id)}
                      className="rounded-lg bg-red-500/90 px-3 py-1 text-xs font-semibold text-white hover:bg-red-500 disabled:opacity-50"
                    >
                      Yes, delete
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmId(null)}
                      className="rounded-lg bg-[var(--color-panel-2)] px-3 py-1 text-xs text-zinc-300 hover:bg-[var(--color-edge)]"
                    >
                      No
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => void openCard(s.id)}
                      className="rounded-lg border border-[var(--color-edge)] px-3 py-1 text-xs font-semibold text-zinc-200 hover:bg-[var(--color-panel-2)]"
                    >
                      Card
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmId(s.id)}
                      title="Delete this session"
                      className="rounded-lg border border-[var(--color-edge)] p-1.5 text-zinc-400 hover:bg-[var(--color-panel-2)] hover:text-red-400"
                    >
                      <IconTrash width={14} height={14} />
                    </button>
                  </>
                )}
              </span>
            </li>
          ))}
        </ul>
        <Pager page={paged.page} pages={paged.pages} onPage={paged.setPage} label="sessions" />
        </>
      )}

      {card ? (
        <div
          className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/80 p-4 backdrop-blur-sm"
          onClick={() => setCard(null)}
        >
          <div
            className="my-auto w-full max-w-md space-y-4 rounded-2xl border border-[var(--color-edge)] bg-[var(--color-panel)] p-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-sm font-semibold text-zinc-200">Your goon card</h2>
            {card.failed ? (
              <div className="flex aspect-[4/5] items-center justify-center rounded-xl bg-[var(--color-ink)] px-6 text-center text-sm text-zinc-400">
                The card couldn&rsquo;t be drawn. Reload and try again.
              </div>
            ) : card.url ? (
              <img src={card.url} alt="Your goon session card" className="w-full rounded-xl border border-[var(--color-edge)]" />
            ) : (
              <div className="flex aspect-[4/5] items-center justify-center rounded-xl bg-[var(--color-ink)]">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-[var(--color-edge)] border-t-[var(--color-accent)]" />
              </div>
            )}
            <div className="flex gap-2">
              <SessionCardDownload url={card.url} source="profile" />
              <button
                type="button"
                onClick={() => setCard(null)}
                className="rounded-xl bg-[var(--color-panel-2)] px-4 py-2.5 text-sm text-zinc-300 hover:bg-[var(--color-edge)]"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}

/** A muted placeholder card for empty profile sections. */
function EmptyHint({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-xl border border-dashed border-[var(--color-edge)] bg-[var(--color-panel)] p-4 text-sm text-zinc-500">
      {children}
    </div>
  );
}

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const WEEKDAY_LABELS = ["M", "", "W", "", "F", "", "S"];
/** Accent ramp: empty + 4 intensities. */
const HEAT_LEVELS = [
  "bg-[var(--color-panel-2)]",
  "bg-[var(--color-accent)]/25",
  "bg-[var(--color-accent)]/45",
  "bg-[var(--color-accent)]/70",
  "bg-[var(--color-accent)]",
];

/** GitHub-style contribution heatmap of daily watch activity (week columns × weekday rows). */
function ContributionGraph({ points }: { points: StatsActivityPoint[] }) {
  // Monday-based weekday (0 = Mon … 6 = Sun), computed in local time so a
  // "YYYY-MM-DD" string doesn't shift a day under UTC parsing.
  const dow = (date: string) => {
    const [y, m, d] = date.split("-").map(Number);
    return (new Date(y, m - 1, d).getDay() + 6) % 7;
  };
  const monthOf = (date: string) => Number(date.slice(5, 7)) - 1;

  // Bucket every day into a week column, padding the first column so day 0
  // lands on its weekday row.
  const weeks: (StatsActivityPoint | null)[][] = [];
  let col: (StatsActivityPoint | null)[] = points.length
    ? new Array<StatsActivityPoint | null>(dow(points[0].date)).fill(null)
    : [];
  for (const p of points) {
    col.push(p);
    if (col.length === 7) {
      weeks.push(col);
      col = [];
    }
  }
  if (col.length) {
    while (col.length < 7) col.push(null);
    weeks.push(col);
  }

  const max = Math.max(1, ...points.map((p) => p.seconds));
  const level = (secs: number) => {
    if (secs <= 0) return 0;
    const r = secs / max;
    return r < 0.25 ? 1 : r < 0.5 ? 2 : r < 0.75 ? 3 : 4;
  };

  // Month label above a column when its first real day starts a new month.
  const monthLabels = weeks.map((w, i) => {
    const first = w.find((c): c is StatsActivityPoint => !!c);
    if (!first) return "";
    const prev =
      i > 0 ? weeks[i - 1].find((c): c is StatsActivityPoint => !!c) : null;
    return i === 0 || (prev && monthOf(prev.date) !== monthOf(first.date))
      ? MONTHS[monthOf(first.date)]
      : "";
  });

  return (
    <div className="overflow-x-auto rounded-xl border border-[var(--color-edge)] bg-[var(--color-panel)] p-4">
      <div
        className="flex gap-[3px]"
        role="img"
        aria-label="Daily gooning activity, last 12 months"
      >
        <div className="mr-1 flex shrink-0 flex-col gap-[3px] pt-[16px] text-[9px] leading-[11px] text-zinc-500">
          {WEEKDAY_LABELS.map((l, i) => (
            <div key={i} className="h-[11px]">
              {l}
            </div>
          ))}
        </div>
        <div className="min-w-max">
          <div className="mb-[3px] flex h-[13px] gap-[3px] text-[9px] leading-none text-zinc-500">
            {monthLabels.map((label, i) => (
              <div key={i} className="relative w-[11px]">
                {label ? (
                  <span className="absolute left-0 top-0 whitespace-nowrap">
                    {label}
                  </span>
                ) : null}
              </div>
            ))}
          </div>
          <div className="flex gap-[3px]">
            {weeks.map((w, ci) => (
              <div key={ci} className="flex flex-col gap-[3px]">
                {w.map((cell, ri) => (
                  <div
                    key={ri}
                    className={cx(
                      "h-[11px] w-[11px] rounded-[2px]",
                      cell
                        ? HEAT_LEVELS[level(cell.seconds)]
                        : "bg-transparent",
                    )}
                    title={
                      cell
                        ? `${cell.date}: ${fmtDurationSecs(cell.seconds)} · ${cell.clips} clip${cell.clips === 1 ? "" : "s"}`
                        : undefined
                    }
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-1.5 text-[10px] text-zinc-500">
        <span>Less</span>
        {HEAT_LEVELS.map((c, i) => (
          <span key={i} className={cx("h-[11px] w-[11px] rounded-[2px]", c)} />
        ))}
        <span>More</span>
        <span className="ml-auto">peak {fmtDurationSecs(max)}/day</span>
      </div>
    </div>
  );
}

function CategoryBreakdown({
  data,
}: {
  data: Array<{ category: string; seconds: number }>;
}) {
  const max = Math.max(1, ...data.map((d) => d.seconds));
  return (
    <div className="space-y-2">
      {data.map((d) => (
        <div key={d.category} className="flex items-center gap-3">
          <span
            className="w-28 shrink-0 truncate text-xs text-zinc-300"
            title={d.category}
          >
            {d.category}
          </span>
          <div className="h-3 flex-1 overflow-hidden rounded-full bg-[var(--color-panel-2)]">
            <div
              className="h-full rounded-full bg-[var(--color-accent)]"
              style={{ width: `${(d.seconds / max) * 100}%` }}
            />
          </div>
          <span className="w-14 shrink-0 text-right text-xs text-zinc-500">
            {fmtDurationSecs(d.seconds)}
          </span>
        </div>
      ))}
    </div>
  );
}
